import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Phone, Mail, MessageCircle, Download, Linkedin, Building2, MapPin, Globe } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamCardPage() {
  const { slug } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/team/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => setMember(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !member) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Card Not Found</h1>
        <p className="text-slate-400">This team member profile does not exist.</p>
      </div>
    </div>
  );

  const whatsappNumber = member.whatsapp || member.phone?.replace(/[^0-9]/g, "");

  const photoFullUrl = member.photo_url
    ? (member.photo_url.startsWith("/api") ? `https://www.amarpreet.in${member.photo_url}` : member.photo_url)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="team-card-page">
      <SEO
        title={`${member.name} - ${member.designation}`}
        description={`${member.name}, ${member.designation} at Amar Preet Scaffolding. Contact: ${member.phone}`}
        canonical={`/team/${member.slug}`}
        image={photoFullUrl}
      />

      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header with brand */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 pt-8 pb-16 text-center relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-yellow-500" />
            <img
              src="/images/aps-logo.png"
              alt="Amar Preet Scaffolding"
              className="h-10 mx-auto mb-2 brightness-0 invert"
            />
            <p className="text-slate-400 text-xs tracking-widest uppercase">Scaffolding & Shuttering Solutions</p>
          </div>

          {/* Photo overlapping header */}
          <div className="flex justify-center -mt-12 relative z-10">
            <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-slate-200">
              {member.photo_url ? (
                <img src={member.photo_url.startsWith("/api") ? `${BACKEND_URL}${member.photo_url}` : member.photo_url} alt={member.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-slate-900 text-3xl font-bold">
                  {member.name?.charAt(0)}
                </div>
              )}
            </div>
          </div>

          {/* Name & Designation */}
          <div className="text-center px-6 pt-4 pb-2">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="card-name">{member.name}</h1>
            <p className="text-yellow-600 font-semibold text-sm mt-1">{member.designation}</p>
            {member.department && (
              <p className="text-slate-500 text-xs mt-1">{member.department}</p>
            )}
          </div>

          {/* Divider */}
          <div className="mx-6 my-4 h-px bg-slate-200" />

          {/* Contact Actions */}
          <div className="px-6 space-y-3">
            {/* Call */}
            <a
              href={`tel:${member.phone}`}
              className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-yellow-50 transition-colors group"
              data-testid="card-call"
            >
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Phone className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Phone</p>
                <p className="text-slate-900 font-semibold text-sm">{member.phone}</p>
              </div>
            </a>

            {/* Email */}
            {member.email && (
              <a
                href={`mailto:${member.email}`}
                className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-yellow-50 transition-colors group"
                data-testid="card-email"
              >
                <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">Email</p>
                  <p className="text-slate-900 font-semibold text-sm">{member.email}</p>
                </div>
              </a>
            )}

            {/* WhatsApp */}
            <a
              href={`https://wa.me/${whatsappNumber}?text=Hi ${member.name}, I got your contact from Amar Preet Scaffolding website.`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-green-50 transition-colors group"
              data-testid="card-whatsapp"
            >
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">WhatsApp</p>
                <p className="text-slate-900 font-semibold text-sm">Send Message</p>
              </div>
            </a>

            {/* LinkedIn */}
            {member.linkedin && (
              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-blue-50 transition-colors group"
                data-testid="card-linkedin"
              >
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Linkedin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">LinkedIn</p>
                  <p className="text-slate-900 font-semibold text-sm">View Profile</p>
                </div>
              </a>
            )}
          </div>

          {/* Save Contact Button */}
          <div className="px-6 py-6">
            <a
              href={`${BACKEND_URL}/api/team/${member.slug}/vcf`}
              className="w-full flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-bold py-3.5 rounded-xl transition-all hover:scale-[1.02] shadow-lg"
              data-testid="save-contact-btn"
            >
              <Download className="w-5 h-5" />
              Save Contact
            </a>
          </div>

          {/* QR Code */}
          <div className="px-6 pb-6 flex flex-col items-center">
            <div className="bg-white border-2 border-slate-100 rounded-xl p-4 inline-block">
              <QRCodeSVG
                value={`https://www.amarpreet.in/team/${member.slug}`}
                size={120}
                bgColor="#ffffff"
                fgColor="#0f172a"
                level="M"
                imageSettings={{
                  src: "/images/aps-logo.png",
                  height: 24,
                  width: 24,
                  excavate: true,
                }}
              />
            </div>
            <p className="text-slate-400 text-xs mt-2">Scan to view this card</p>
          </div>

          {/* Company Footer */}
          <div className="bg-slate-50 px-6 py-5 border-t border-slate-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-sm">Amar Preet Scaffolding</p>
                <p className="text-slate-500 text-xs mt-0.5">Premium Scaffolding & Shuttering Rental</p>
                <div className="flex items-center gap-1 mt-1.5 text-slate-400 text-xs">
                  <MapPin className="w-3 h-3" />
                  <span>Delhi | Noida | Hyderabad | Vizag</span>
                </div>
                <div className="flex items-center gap-1 mt-1 text-slate-400 text-xs">
                  <Globe className="w-3 h-3" />
                  <a href="https://www.amarpreet.in" className="hover:text-yellow-600 transition-colors">www.amarpreet.in</a>
                </div>
                <a
                  href="https://www.linkedin.com/company/amarpreetscaffolding"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-blue-600 text-xs hover:text-blue-700 transition-colors"
                >
                  <Linkedin className="w-3 h-3" />
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Powered by */}
        <p className="text-center text-slate-600 text-xs mt-6">
          Amar Preet Scaffolding &middot; 30+ Years of Excellence
        </p>
      </div>
    </div>
  );
}
